import { ContentCopy } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axios, { useApiRequest } from "../../api/axios";
import {
  buildClientInvoiceSettingsEndpoint,
  buildClientLastInvoiceDefaultsEndpoint,
  CLIENT_INVOICE_SETTINGS_UPDATED_EVENT,
  ERROR_SNACKBAR,
  INVOICE_GROUPING_LABEL_KEYS,
  INVOICE_GROUPING_OPTIONS,
  INVOICE_PAYMENT_METHOD_LABEL_KEYS,
  INVOICE_PAYMENT_METHOD_OPTIONS,
  NUMERATION_SCHEMA_ENDPOINT,
  SUCCESS_SNACKBAR,
  VAT_RATE_LABEL_KEYS,
  VAT_RATE_OPTIONS,
} from "../../consts";
import {
  ClientInvoiceSettings,
  LastInvoiceDefaults,
  WorkspaceInvoiceDefaults,
} from "../../types/invoices";
import { NumerationSchema } from "../../types/numerationSchemas";
import { formatDate } from "../../utils";
import CustomCircularProgress from "../ui/customCircularProgress/customCircularProgress.component";
import { OptionRecord } from "../ui/customAutocomplete/customAutocomplete.component";
import ReceiverFields from "./receiverFields.component";

/** DRF returns field errors as `{field: [{message, code}]}`. */
function getError(field: string, error: any): string | null {
  const fieldErrors = error?.[field];
  return Array.isArray(fieldErrors) && fieldErrors.length > 0
    ? fieldErrors[0]?.message ?? null
    : null;
}

interface ClientInvoiceSettingsFormProps {
  clientId: string | number;
  onSuccess?: () => void;
}

/**
 * Edits a client's own invoice defaults. Every field may be left on
 * "Workspace default", which keeps the client following the workspace setting
 * - including later changes to it.
 */
export default function ClientInvoiceSettingsForm({
  clientId,
  onSuccess,
}: ClientInvoiceSettingsFormProps) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { loading: saving, executeRequest } = useApiRequest();

  const [loaded, setLoaded] = useState<boolean>(false);
  const [serverErrors, setServerErrors] = useState<any>(null);
  const [workspace, setWorkspace] = useState<WorkspaceInvoiceDefaults | null>(
    null
  );
  const [numerationSchemas, setNumerationSchemas] = useState<NumerationSchema[]>(
    []
  );
  const [lastInvoice, setLastInvoice] = useState<LastInvoiceDefaults | null>(
    null
  );
  const [copiedFrom, setCopiedFrom] = useState<
    LastInvoiceDefaults["invoice"] | null
  >(null);

  const [vatRate, setVatRate] = useState<string>("");
  const [paymentMethod, setPaymentMethod] = useState<string>("");
  const [paymentTermDays, setPaymentTermDays] = useState<string>("");
  const [numerationSchemaId, setNumerationSchemaId] = useState<string>("");
  const [receiver, setReceiver] = useState<OptionRecord | null>(null);
  const [receiverRole, setReceiverRole] = useState<string>("");
  const [invoiceGrouping, setInvoiceGrouping] = useState<string>("");

  useEffect(() => {
    let active = true;

    axios
      .get<ClientInvoiceSettings>(buildClientInvoiceSettingsEndpoint(clientId))
      .then(({ data }) => {
        if (!active) return;
        setWorkspace(data.workspace_defaults);
        setVatRate(data.vat_rate);
        setPaymentMethod(data.payment_method);
        setPaymentTermDays(
          data.payment_term_days !== null ? String(data.payment_term_days) : ""
        );
        setNumerationSchemaId(
          data.numeration_schema_id !== null
            ? String(data.numeration_schema_id)
            : ""
        );
        setReceiver(
          data.receiver_id !== null
            ? { id: data.receiver_id, name: data.receiver_name ?? "" }
            : null
        );
        setReceiverRole(data.receiver_role);
        setInvoiceGrouping(data.invoice_grouping);
      })
      .catch(() => {})
      .finally(() => active && setLoaded(true));

    // A 404 only means the client has never been invoiced - the copy button
    // then stays disabled rather than reporting an error.
    axios
      .get<LastInvoiceDefaults>(buildClientLastInvoiceDefaultsEndpoint(clientId))
      .then(({ data }) => active && setLastInvoice(data))
      .catch(() => active && setLastInvoice(null));

    axios
      .get(NUMERATION_SCHEMA_ENDPOINT)
      .then(({ data }) => {
        const schemas = Array.isArray(data?.results) ? data.results : data;
        if (active) setNumerationSchemas(Array.isArray(schemas) ? schemas : []);
      })
      .catch(() => {});

    return () => {
      active = false;
    };
  }, [clientId]);

  /**
   * Fill the form from the last invoice. A value that matches the workspace
   * default is left on "Workspace default" instead of being pinned, so the
   * client keeps following the workspace if that setting changes later.
   */
  const copyFromLastInvoice = () => {
    if (!lastInvoice) return;

    const unlessWorkspace = (value: string, workspaceValue?: string | number | null) =>
      workspaceValue !== undefined &&
      workspaceValue !== null &&
      String(workspaceValue) === value
        ? ""
        : value;

    setVatRate(unlessWorkspace(lastInvoice.vat_rate, workspace?.vat_rate));
    setPaymentMethod(
      unlessWorkspace(lastInvoice.payment_method, workspace?.payment_method)
    );
    setPaymentTermDays(
      unlessWorkspace(
        String(lastInvoice.payment_term_days),
        workspace?.payment_term_days
      )
    );
    setNumerationSchemaId(
      lastInvoice.numeration_schema_id !== null
        ? unlessWorkspace(
            String(lastInvoice.numeration_schema_id),
            workspace?.numeration_schema_id
          )
        : ""
    );
    setReceiver(
      lastInvoice.receiver_id !== null
        ? { id: lastInvoice.receiver_id, name: lastInvoice.receiver_name ?? "" }
        : null
    );
    setReceiverRole(lastInvoice.receiver_role);
    setServerErrors(null);
    setCopiedFrom(lastInvoice.invoice);
  };

  const isValid = !receiver || !!receiverRole;

  const handleSubmit = async () => {
    setServerErrors(null);

    const result = await executeRequest(() =>
      axios.put(buildClientInvoiceSettingsEndpoint(clientId), {
        vat_rate: vatRate,
        payment_method: paymentMethod,
        payment_term_days: paymentTermDays === "" ? null : Number(paymentTermDays),
        numeration_schema_id: numerationSchemaId || null,
        receiver_id: receiver?.id ?? null,
        receiver_role: receiver ? receiverRole : "",
        invoice_grouping: invoiceGrouping,
      })
    );

    if (result?.success) {
      enqueueSnackbar(t("Form was succesfully processed."), SUCCESS_SNACKBAR);
      window.dispatchEvent(new Event(CLIENT_INVOICE_SETTINGS_UPDATED_EVENT));
      onSuccess?.();
      return;
    }

    setServerErrors(result?.errorData);
    enqueueSnackbar(
      result?.errorData?.message ||
        t("There was an error, please review the form."),
      ERROR_SNACKBAR
    );
  };

  if (!loaded) {
    return <CustomCircularProgress />;
  }

  const schemaName = (id: number | null | undefined) =>
    numerationSchemas.find((schema) => String(schema.id) === String(id))?.name;

  /** "Workspace default (23%)" - so an empty choice says what it resolves to. */
  const workspaceOption = (resolved?: string | null) => (
    <MenuItem value="">
      <em>
        {resolved
          ? t("Workspace default ({{value}})", { value: resolved })
          : t("Workspace default")}
      </em>
    </MenuItem>
  );

  return (
    <Stack spacing={2} sx={{ minWidth: { xs: "auto", md: 640 } }}>
      <Box>
        <Tooltip
          title={lastInvoice ? "" : t("This client has no invoices yet")}
        >
          {/* A disabled button fires no events, so the tooltip needs a wrapper. */}
          <span>
            <Button
              size="small"
              variant="outlined"
              startIcon={<ContentCopy />}
              onClick={copyFromLastInvoice}
              disabled={!lastInvoice}
            >
              {t("Copy from last invoice")}
            </Button>
          </span>
        </Tooltip>
      </Box>

      {copiedFrom && (
        <Alert severity="info">
          {copiedFrom.number
            ? t("Filled in from invoice {{number}}.", {
                number: copiedFrom.number,
              })
            : t("Filled in from the draft invoice of {{date}}.", {
                date: formatDate(copiedFrom.issued_at),
              })}{" "}
          {t(
            "Values matching the workspace defaults are left to follow them. Review and save."
          )}
        </Alert>
      )}

      <Typography variant="subtitle2">{t("Invoice defaults")}</Typography>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <TextField
          size="small"
          select
          fullWidth
          label={t("VAT rate")}
          value={vatRate}
          onChange={(event) => setVatRate(event.target.value)}
          error={!!getError("vat_rate", serverErrors)}
          helperText={getError("vat_rate", serverErrors)}
        >
          {workspaceOption(
            workspace ? t(VAT_RATE_LABEL_KEYS[workspace.vat_rate] ?? workspace.vat_rate) : null
          )}
          {VAT_RATE_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {t(option.labelKey)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          select
          fullWidth
          label={t("Payment method")}
          value={paymentMethod}
          onChange={(event) => setPaymentMethod(event.target.value)}
          error={!!getError("payment_method", serverErrors)}
          helperText={getError("payment_method", serverErrors)}
        >
          {workspaceOption(
            workspace
              ? t(
                  INVOICE_PAYMENT_METHOD_LABEL_KEYS[workspace.payment_method] ??
                    workspace.payment_method
                )
              : null
          )}
          {INVOICE_PAYMENT_METHOD_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {t(option.labelKey)}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <TextField
          size="small"
          fullWidth
          type="number"
          label={t("Payment term days")}
          value={paymentTermDays}
          onChange={(event) => {
            if (/^\d{0,3}$/.test(event.target.value)) {
              setPaymentTermDays(event.target.value);
            }
          }}
          slotProps={{ htmlInput: { min: 0 } }}
          error={!!getError("payment_term_days", serverErrors)}
          helperText={
            getError("payment_term_days", serverErrors) ??
            (workspace
              ? t("Leave empty to use the workspace default ({{days}} days).", {
                  days: workspace.payment_term_days,
                })
              : null)
          }
        />
        <TextField
          size="small"
          select
          fullWidth
          label={t("Numeration schema")}
          value={
            numerationSchemas.some(
              (schema) => String(schema.id) === numerationSchemaId
            )
              ? numerationSchemaId
              : ""
          }
          onChange={(event) => setNumerationSchemaId(event.target.value)}
          error={!!getError("numeration_schema_id", serverErrors)}
          helperText={getError("numeration_schema_id", serverErrors)}
        >
          {workspaceOption(schemaName(workspace?.numeration_schema_id))}
          {numerationSchemas.map((schema) => (
            <MenuItem key={schema.id} value={String(schema.id)}>
              {schema.name}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      <Divider />

      <Typography variant="subtitle2">{t("Automated invoicing")}</Typography>

      <TextField
        size="small"
        select
        fullWidth
        label={t("Invoice grouping")}
        value={invoiceGrouping}
        onChange={(event) => setInvoiceGrouping(event.target.value)}
        error={!!getError("invoice_grouping", serverErrors)}
        helperText={
          getError("invoice_grouping", serverErrors) ??
          t(
            "How settlement automation gathers this client's confirmed settlements into invoices. Invoices created by hand from a settlement always cover that one settlement."
          )
        }
      >
        {workspaceOption(
          workspace
            ? t(
                INVOICE_GROUPING_LABEL_KEYS[workspace.invoice_grouping] ??
                  workspace.invoice_grouping
              )
            : null
        )}
        {INVOICE_GROUPING_OPTIONS.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {t(option.labelKey)}
          </MenuItem>
        ))}
      </TextField>

      <Divider />

      <Box>
        <Typography variant="subtitle2">{t("Receiver")}</Typography>
        <Typography variant="caption" color="text.secondary">
          {t(
            "Put on every new invoice for this client, including invoices issued by settlement automation."
          )}
        </Typography>
      </Box>

      <ReceiverFields
        receiver={receiver}
        onReceiverChange={(value) => {
          setReceiver(value);
          if (!value) setReceiverRole("");
        }}
        role={receiverRole}
        onRoleChange={setReceiverRole}
        receiverError={getError("receiver_id", serverErrors)}
        roleError={getError("receiver_role", serverErrors)}
      />

      <Button
        variant="contained"
        onClick={handleSubmit}
        disabled={saving || !isValid}
      >
        {t("Save invoice defaults")}
      </Button>
    </Stack>
  );
}
